import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/mocks/videos";

interface HeroCarouselProps {
  items: Video[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const active = items[current];

  const goPrev = () => setCurrent((prev) => (prev - 1 + items.length) % items.length);
  const goNext = () => setCurrent((prev) => (prev + 1) % items.length);

  return (
    <section className="relative h-[480px] w-full overflow-hidden md:h-[620px]">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} - 多多视频焦点推荐`}
            title={`${item.title} - 多多视频`}
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/85 to-background-950/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/50" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] flex-col justify-center px-4 md:px-6">
        <div key={active.id} className="ddv-fade-up max-w-xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-background-950">
              焦点推荐
            </span>
            <span className="rounded-md bg-primary-500/90 px-2.5 py-1 text-xs font-semibold text-background-50">
              {active.categoryName}
            </span>
            <span className="flex items-center gap-1 rounded-md bg-background-800/80 px-2.5 py-1 text-xs font-semibold text-accent-400">
              <i className="ri-star-fill text-xs" />
              {active.rating.toFixed(1)}
            </span>
          </div>
          <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-foreground-50 md:text-5xl">
            {active.title}
          </h2>
          <p className="mt-3 text-sm text-foreground-300 md:text-base">
            {active.region} · {active.year} · {active.duration} · {active.status}
          </p>
          <p className="mt-4 line-clamp-2 max-w-lg text-sm leading-relaxed text-foreground-300 md:text-base">
            {active.description}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-600 cursor-pointer"
            >
              <i className="ri-play-fill text-lg" />
              立即播放
            </Link>
            <Link
              to={`/category/${active.category}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-300/40 bg-background-950/40 px-6 py-3 text-sm font-semibold text-foreground-100 transition-colors hover:border-accent-400 hover:text-accent-400 cursor-pointer"
            >
              <i className="ri-compass-3-line text-lg" />
              更多{active.categoryName}
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={goPrev}
        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/50 text-foreground-100 transition-colors hover:bg-accent-500 hover:text-background-950 md:left-6 md:h-11 md:w-11 cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={goNext}
        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/50 text-foreground-100 transition-colors hover:bg-accent-500 hover:text-background-950 md:right-6 md:h-11 md:w-11 cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setCurrent(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === current ? "w-7 bg-accent-500" : "w-3 bg-foreground-300/50 hover:bg-foreground-300"
            }`}
          />
        ))}
      </div>
    </section>
  );
}